/**
 * SEO Configuration File
 * Manage all SEO-related metadata including title, description, canonical URLs, and more
 */

// Base URL - Update this with your production domain
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://vatcalculators.app';

// Default SEO configuration
export const defaultSEO = {
  title: 'Free VAT Calculator Online | Add or Remove VAT Instantly',
  description: 'Fast and accurate VAT calculator to add or remove VAT instantly. Calculate VAT inclusive or exclusive prices with ease. Free, secure, and easy to use.',
  canonical: `${BASE_URL}/`,
  keywords: 'VAT calculator, tax calculator, value added tax, VAT tool, business calculator, tax tool, VAT rate calculator, add VAT, remove VAT',
  author: 'VAT Calculator',
  robots: 'index, follow',
  themeColor: '#6366f1',
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    ...defaultSEO,
    title: 'Free VAT Calculator Online | Add or Remove VAT Instantly',
    description: 'Fast and accurate VAT calculator to add or remove VAT instantly. Calculate VAT inclusive or exclusive prices with ease. Free, secure, and easy to use.',
    canonical: `${BASE_URL}/`,
  },
  calculator: {
    ...defaultSEO,
    title: 'Free VAT Calculator - Add or Remove VAT Instantly',
    description: 'Calculate VAT instantly with our free online calculator. Add VAT to net amounts or remove VAT from gross amounts. Support for multiple countries and currencies.',
    canonical: `${BASE_URL}/#calculator`,
  },
  features: {
    ...defaultSEO,
    title: 'VAT Calculator Features - Why Choose Our Calculator',
    description: 'Discover powerful features of our VAT calculator: dual-mode functionality, multi-currency support, real-time calculations, calculation history, and privacy-focused design.',
    canonical: `${BASE_URL}/#features`,
  },
  faq: {
    ...defaultSEO,
    title: 'VAT Calculator FAQ - Frequently Asked Questions',
    description: 'Find answers to common questions about VAT calculation, usage, data privacy, and more. Learn how to use our VAT calculator effectively.',
    canonical: `${BASE_URL}/#faq`,
  },
  about: {
    ...defaultSEO,
    title: 'About Us - VAT Calculator',
    description: 'Learn about VAT Calculator, your trusted partner for accurate and efficient VAT calculations. Our mission is to provide fast, accurate, and easy-to-use tools for businesses and individuals worldwide.',
    canonical: `${BASE_URL}/about`,
  },
  privacy: {
    ...defaultSEO,
    title: 'Privacy Policy - VAT Calculator',
    description: 'Read our Privacy Policy to understand how VAT Calculator protects your privacy. We do not collect or store personal information - all calculations are performed locally in your browser.',
    canonical: `${BASE_URL}/privacy-policy`,
  },
  terms: {
    ...defaultSEO,
    title: 'Terms and Conditions - VAT Calculator',
    description: 'Review the Terms and Conditions for using VAT Calculator. Understand your rights and responsibilities when using our free VAT calculation service.',
    canonical: `${BASE_URL}/terms`,
  },
  contact: {
    ...defaultSEO,
    title: 'Contact Us - VAT Calculator',
    description: 'Get in touch with VAT Calculator. Have questions, feedback, or need support? Contact us and we\'ll respond as soon as possible.',
    canonical: `${BASE_URL}/contact`,
  },
};

/**
 * Get SEO configuration for a specific page
 * @param {string} page - Page identifier (e.g., 'home', 'calculator', 'features', 'faq')
 * @returns {object} SEO configuration object
 */
export const getSEO = (page = 'home') => {
  return pageSEO[page] || defaultSEO;
};

/**
 * Update document head with SEO metadata
 * @param {object} config - SEO configuration object
 */
export const updateSEO = (config) => {
  if (typeof document === 'undefined') return;

  // Update title
  document.title = config.title;

  // Update or create meta tags
  const updateMetaTag = (name, content, attribute = 'name') => {
    if (!content) return;
    
    let element = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  // Basic meta tags
  updateMetaTag('description', config.description);
  updateMetaTag('keywords', config.keywords || '');
  updateMetaTag('author', config.author || '');
  updateMetaTag('robots', config.robots || 'index, follow');
  updateMetaTag('theme-color', config.themeColor || '#6366f1');

  // Update canonical link
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', config.canonical);
};

/**
 * Generate structured data (JSON-LD) for SEO
 * @param {object} config - SEO configuration object
 * @returns {string} JSON-LD script content
 */
export const generateStructuredData = (config) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: config.title,
    description: config.description,
    url: config.canonical,
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '1250',
    },
  };

  return JSON.stringify(structuredData);
};

/**
 * Add structured data to document head
 * @param {object} config - SEO configuration object
 */
export const addStructuredData = (config) => {
  if (typeof document === 'undefined') return;

  // Remove existing structured data if any
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = generateStructuredData(config);
  document.head.appendChild(script);
};

/**
 * Initialize SEO for a page
 * @param {string} page - Page identifier
 */
export const initSEO = (page = 'home') => {
  const config = getSEO(page);
  updateSEO(config);
  addStructuredData(config);
};
